import React, { useState } from "react";
import { X } from "lucide-react";
import { cn } from "../../../lib/cn";

/**
 * Chips — ui-v2.5. Lista de valores, eliminables uno a uno. Especificación: catálogo · sección Chips.
 *
 * Dos exports:
 *  - <Chips value onChange removable input? placeholder? /> — lista editable.
 *      value: array de strings U objetos { label, color?, icon?, meta? }.
 *      input=true añade el campo de entrada múltiple (Enter y coma añaden; Backspace borra el último).
 *  - <ChipSelect options value onChange multiple? /> — chips seleccionables, no eliminables.
 *      options: [{ value, label, color? }]. value: array (multiple) o valor suelto.
 */

function itemParts(item) {
  if (typeof item === "string") return { label: item, color: null, Icon: null, meta: null };
  return { label: item.label, color: item.color || null, Icon: item.icon || null, meta: item.meta || null };
}

export function Chips({ value = [], onChange, removable = true, input = false, placeholder, className }) {
  const [draft, setDraft] = useState("");
  const remove = (i) => onChange?.(value.filter((_, idx) => idx !== i));
  const add = (text) => {
    const t = text.trim();
    if (t && onChange) onChange([...value, t]);
  };
  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(draft);
      setDraft("");
    } else if (e.key === "Backspace" && !draft && value.length) {
      remove(value.length - 1);
    }
  };

  const chip = (item, i) => {
    const { label, color, Icon, meta } = itemParts(item);
    return (
      <span
        key={i}
        className="inline-flex items-center gap-1.5 rounded-v25-full border border-v25-line bg-v25-surface-raised py-1 pl-2.5 pr-1 text-xs text-v25-ink-700"
      >
        {color ? (
          <span className="h-2 w-2 rounded-v25-full" style={{ background: color }} />
        ) : Icon ? (
          <Icon className="h-3 w-3" />
        ) : null}
        {label}
        {meta && <span className="text-[10px] text-v25-ink-400">{meta}</span>}
        {removable && (
          <button
            type="button"
            onClick={() => remove(i)}
            aria-label={`Quitar ${label}`}
            className="grid h-4 w-4 place-items-center rounded-v25-full text-v25-ink-400 transition-colors hover:bg-v25-line hover:text-red-500 focus-visible:outline-none focus-visible:shadow-v25-focus"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        )}
      </span>
    );
  };

  if (input) {
    return (
      <div className={cn("flex flex-wrap items-center gap-1.5 rounded-v25-md border border-v25-line-strong bg-white p-2", className)}>
        {value.map(chip)}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="min-w-[140px] flex-1 border-0 bg-transparent px-1 text-sm outline-none placeholder:text-v25-ink-400"
        />
      </div>
    );
  }

  return <div className={cn("flex flex-wrap gap-2", className)}>{value.map(chip)}</div>;
}

export function ChipSelect({ options = [], value, onChange, multiple = true, className }) {
  const isSel = (v) => (Array.isArray(value) ? value.includes(v) : value === v);
  const toggle = (v) => {
    if (!onChange) return;
    if (multiple) onChange(isSel(v) ? value.filter((x) => x !== v) : [...(value || []), v]);
    else onChange(v);
  };
  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      {options.map((o) => {
        const sel = isSel(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={sel}
            onClick={() => toggle(o.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-v25-full border px-3.5 py-2 text-[13px] transition-colors focus-visible:outline-none focus-visible:shadow-v25-focus",
              sel
                ? "border-v25-brand-600 bg-v25-prefill-bg text-v25-brand-800"
                : "border-v25-line bg-v25-surface-raised text-v25-ink-700 hover:bg-v25-surface-sunken"
            )}
          >
            {o.color && <span className="h-2 w-2 rounded-v25-full" style={{ background: o.color }} />}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
